import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { buttonClasses } from '../../components/ui/Button';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseStatuses } from '../../types/admin';
import type { CaseStatus } from '../../types/admin';
import { formatDate, formatPrice } from '../../utils/format';
type Filter = 'Alle' | CaseStatus;
export function Cases() {
  const {
    cases,
    customerById
  } = useAdminData();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('Alle');
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return cases.filter(item => {
      const customer = customerById(item.customerId);
      const matchesFilter = filter === 'Alle' || item.status === filter;
      const haystack = `${item.number} ${item.title} ${item.address} ${customer?.name ?? item.customerName}`.toLowerCase();
      return matchesFilter && (!term || haystack.includes(term));
    });
  }, [cases, customerById, filter, query]);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1061" data-mp="3gdkq9qltk4ra">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1062" data-mp="3ubslnu37iy0c">
        <div data-id="element-1063" data-mp="3pbepb9ayxci5">
          <p className="text-sm text-ink-muted" data-id="element-1064" data-mp="3pbmyaq3yzxo2">{cases.length} sager i alt</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1065" data-mp="83j6eqn0cao6">
            Sager
          </h1>
        </div>
        <Link to="/admin/sager/ny" className={buttonClasses('primary', 'md')} data-id="element-1066" data-mp="32vk2i9q4i0a">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1067" data-mp="gvbewvsh19q4" />
          Opret sag
        </Link>
      </div>

      <div className="mt-6" data-id="element-1068" data-mp="cwm5tfegueca">
        <label htmlFor="sagsoegning" className="sr-only" data-id="element-1069" data-mp="qu0zwxfnzaal">
          Søg i sager
        </label>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3" data-id="element-1070" data-mp="lv7hcqlist3k">
          <SearchIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-1071" data-mp="3fn5s98qvbzoj" />
          <input id="sagsoegning" value={query} onChange={event => setQuery(event.target.value)} placeholder="Søg på kunde, sagsnummer, opgave eller adresse" className="w-full bg-transparent text-[15px] text-ink placeholder:text-ink-faint focus:outline-none" data-id="element-1072" data-mp="3flcmrwrzrli4" />
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filtrér sager efter status" data-id="element-1073" data-mp="277xlaj3q5kf">
          {(['Alle', ...caseStatuses] as Filter[]).map(option => {
          const active = filter === option;
          return <button key={option} type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-colors duration-200 ease-smooth ${active ? 'border-accent/50 bg-accent/15 text-ink' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-1074" data-mp="g6zgnpoh6c5a">
                {option}
              </button>;
        })}
        </div>
      </div>

      <div className="mt-6 rounded-card border border-line bg-white" data-id="element-1075" data-mp="1vs1ghed35s45">
        <div className="hidden md:block" data-id="element-1076" data-mp="2tei9i7esdjk3">
          <table className="w-full text-left text-[15px]" data-id="element-1077" data-mp="2tdfi7ydjxf3n">
            <thead data-id="element-1078" data-mp="2of5oztbe0oko">
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-1079" data-mp="2ogp59jx1mzmg">
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1080" data-mp="2jfjdbob7jgdz">Sag</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1081" data-mp="2ff93g0kao5tu">Kunde</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1082" data-mp="2fhbsah9pnswz">Opgave</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1083" data-mp="182qoal9sx61m">Adresse</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1084" data-mp="13z10vz4v4ui6">Planlagt</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1085" data-mp="2rw8iquptzya4">Status</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1086" data-mp="2wmyowa63055v">Pris</th>
                <th scope="col" className="px-5 py-3" data-id="element-1087" data-mp="2wpbglf1n5tw4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1088" data-mp="2hw9az018kbyb">
              {visible.map(item => <tr key={item.id} className="transition-colors duration-200 ease-smooth hover:bg-canvas" data-id="element-1089" data-mp="2mxow0s3eyigp">
                  <td className="px-5 py-3.5 font-medium text-ink" data-id="element-1090" data-mp="3akx056p3vlh5">#{item.number}</td>
                  <td className="px-5 py-3.5 text-ink" data-id="element-1091" data-mp="3flbaive6j7ic">
                    {customerById(item.customerId)?.name ?? item.customerName}
                  </td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-1092" data-mp="35n5aykfuidgn">{item.title}</td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-1093" data-mp="1q769fs0qhfpd">{item.address || '—'}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1094" data-mp="1q9iwukp9gi9t">{formatDate(item.plannedDate)}</td>
                  <td className="px-5 py-3.5" data-id="element-1095" data-mp="1moyxqb6hxl06"><StatusBadge status={item.status} data-id="element-1096" data-mp="1mofnzhr022x8" /></td>
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-1097" data-mp="1mr1urf62geee">
                    {formatPrice(item.estimate || item.fixedPrice)}
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-1098" data-mp="1wmn3dw5n4cx4">
                    <Link to={`/admin/sager/${item.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-1099" data-mp="1ro25429y83bb">
                      Åbn
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <ul className="divide-y divide-line md:hidden" data-id="element-1100" data-mp="1doka35627tti">
          {visible.map(item => <li key={item.id} data-id="element-1101" data-mp="1dqdlexrmb27u">
              <Link to={`/admin/sager/${item.id}`} className="block px-5 py-4" data-id="element-1102" data-mp="18xaqal76zkpj">
                <div className="flex items-start justify-between gap-3" data-id="element-1103" data-mp="l7zuli4p8gi9">
                  <div className="min-w-0" data-id="element-1104" data-mp="l656xw21maah">
                    <p className="text-xs text-ink-faint" data-id="element-1105" data-mp="g9ycp0j0en8d">Sag #{item.number}</p>
                    <p className="mt-1 truncate text-[15px] font-medium text-ink" data-id="element-1106" data-mp="15xk768g7az3w">{item.title}</p>
                    <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-1107" data-mp="19ynn0uy90v54">
                      {customerById(item.customerId)?.name ?? item.customerName}
                    </p>
                  </div>
                  <StatusBadge status={item.status} data-id="element-1108" data-mp="1jofkbwxqg2da" />
                </div>
                <p className="mt-2 text-sm text-ink-soft" data-id="element-1109" data-mp="1jqjngcnt4xci">
                  {formatDate(item.plannedDate)} · {formatPrice(item.estimate || item.fixedPrice)}
                </p>
              </Link>
            </li>)}
        </ul>

        {visible.length === 0 && <p className="px-5 py-12 text-center text-sm text-ink-muted" data-id="element-1110" data-mp="vxjomoq5on0r">
            Ingen sager matcher søgningen.
          </p>}
      </div>
    </div>;
}